import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const fixTextContrastSkill: Skill = {
    name: SKILL_NAMES.fixTextContrast,
    description:
        'Fixing text that is hard to read against what sits behind it. Load when a tool finding reports low text contrast.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Fixing Text Contrast

A contrast finding says the text and what is behind it are too close in brightness. It does
**not** say the media is too bright — that is one cause among several, and reaching for it
first is how a scene ends up dimmed into mud.

## Look before you dial
Render the scene with \`${TOOL_NAMES.imageAsk}\` and answer one question: **what is the text
actually sitting on?**
- **Bare media** — the photo or video shows through behind the glyphs.
- **A design-drawn backdrop** — a panel, chip, card, or banner the scene design paints behind
  the text.

The two take opposite fixes, and the serialized state will not tell you which you have.

## Text on a design-drawn panel: brightness is the wrong dial
The panel is the backdrop. The media underneath it is not what the text sits on, so dimming
the media changes nothing the reader sees and the finding will come back unchanged. Change
the **text colour** with \`${TOOL_NAMES.setText}\` (\`color\`) so it opposes the panel — or, if
the panel colour is yours to change, change that. Do not touch media brightness.

## Bare text on media: step the brightness down
Use \`${TOOL_NAMES.setMedia}\` and move one step: 1.0 → 0.75 → 0.5.

**0.5 is the floor, and never dim the same scene twice.** If you have already lowered
brightness on a scene and the finding is still there, brightness is not the answer — this
media is wrong for text over it. Escalate; do not take another step.

## Escalation, in order
1. **Flip the text to its opposite pole** — \`${TOOL_NAMES.setText}\` with \`color\`: white on a
   dark backdrop, near-black on a light one. Half-measures (grey on grey) buy nothing.
2. **Move the text somewhere calmer** — \`${TOOL_NAMES.setTextPositionPreset}\`. A photo usually
   has a plain region; put the text there rather than fighting the busy one.
3. **Swap the media** — \`${TOOL_NAMES.searchMedia}\` for an image with an uncluttered area, then
   \`${TOOL_NAMES.setMedia}\`.

## Stop after two attempts per finding
The reported ratio is a mean over the text's whole box, so a small residual shortfall on a
busy photo is not worth a third round. After two attempts, stop and tell the user which
scene you left short and why.

## Do not
- **Don't reach for \`${TOOL_NAMES.setGlobalColorGrading}\`** to fix one scene. It repaints the
  whole video to solve a local problem — see the ${SKILL_NAMES.applyBrandKit} skill.
- **Don't change two dials for one finding.** If you dimmed and then recoloured the text, put
  the brightness back before you finish.`;
    },
};
